import {
  buildTimeSlotLabels,
  formatMinutesLabel,
  minutesToIso,
  parseTimeToMinutes,
} from '@/lib/calendar/dayCalendar'

export const RECURRENCE_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
]

/**
 * @param {string} openTime
 * @param {string} closeTime
 * @param {number} slotMinutes
 * @param {number} [durationMinutes]
 */
export function buildBookableTimeOptions(openTime, closeTime, slotMinutes, durationMinutes = 15) {
  const openMinutes = parseTimeToMinutes(openTime)
  const closeMinutes = parseTimeToMinutes(closeTime)
  const latestStart = Math.max(openMinutes, closeMinutes - durationMinutes)
  const options = []

  for (let cursor = openMinutes; cursor <= latestStart; cursor += slotMinutes) {
    const label = formatMinutesLabel(cursor)
    options.push({ value: label, label })
  }

  return options
}

/**
 * @param {string} selectedDate
 * @param {string} [defaultTime]
 */
export function emptyBookForm(selectedDate, defaultTime = '10:00') {
  return {
    customer: null,
    serviceId: '',
    stylistId: '',
    date: selectedDate,
    time: defaultTime,
    source: 'staff',
    notes: '',
    isBridalEvent: false,
    recurring: {
      enabled: false,
      frequency: 'weekly',
      occurrences: 4,
    },
  }
}

/**
 * @param {ReturnType<typeof emptyBookForm>} form
 */
export function validateBookForm(form) {
  const errors = []

  if (!form.customer?.id) {
    errors.push('Select a guest.')
  }

  if (!form.serviceId) {
    errors.push('Select a service.')
  }

  if (!form.date) {
    errors.push('Select a date.')
  }

  if (!form.time) {
    errors.push('Select a time.')
  }

  if (form.recurring?.enabled) {
    const count = Number(form.recurring.occurrences)
    if (!Number.isFinite(count) || count < 2 || count > 26) {
      errors.push('Recurring bookings need 2–26 occurrences.')
    }
  }

  return errors
}

/**
 * @param {ReturnType<typeof emptyBookForm>} form
 */
export function buildBookPayload(form) {
  const stylistId = form.stylistId ? Number(form.stylistId) : null

  /** @type {Record<string, unknown>} */
  const payload = {
    customer_id: form.customer.id,
    starts_at: minutesToIso(form.date, parseTimeToMinutes(form.time)),
    stylist_id: stylistId,
    source: form.source || 'staff',
    notes: form.notes?.trim() || null,
    is_bridal_event: Boolean(form.isBridalEvent),
    services: [
      {
        service_id: Number(form.serviceId),
        stylist_id: stylistId,
      },
    ],
  }

  if (form.recurring?.enabled) {
    payload.recurrence = {
      frequency: form.recurring.frequency,
      occurrences: Number(form.recurring.occurrences),
    }
  }

  return payload
}

/**
 * @param {import('@/types/api').Service[]} services
 * @param {string | number} serviceId
 */
export function findServiceById(services, serviceId) {
  return services.find((service) => service.id === Number(serviceId)) ?? null
}

/**
 * @param {string} openTime
 * @param {string} closeTime
 * @param {number} slotMinutes
 */
export function defaultBookTime(openTime, closeTime, slotMinutes) {
  const options = buildTimeSlotLabels(
    parseTimeToMinutes(openTime),
    parseTimeToMinutes(closeTime),
    slotMinutes,
  )

  return options[0]?.label ?? openTime
}
